import React, { useState } from 'react';
import { Modal, Form, Row, Col, Alert, Card, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCog, 
  faServer, 
  faUser, 
  faLock, 
  faPlug, 
  faCheckCircle, 
  faExclamationTriangle,
  faTimes,
  faEye,
  faEyeSlash,
  faEnvelope,
  faShieldAlt
} from '@fortawesome/free-solid-svg-icons';
import Button from '../../../components/base/Button';

// Interface para la configuración de email
interface EmailConfig {
  server: string;
  username: string;
  password: string;
  imapPort: number;
  smtpPort: number;
  ssl: boolean;
  displayName: string;
}

interface EmailSettingsModalProps {
  show: boolean;
  onHide: () => void;
  onSave: (config: EmailConfig) => void;
  loading?: boolean;
}

const EmailSettingsModal: React.FC<EmailSettingsModalProps> = ({ 
  show, 
  onHide, 
  onSave, 
  loading = false 
}) => {
  const [config, setConfig] = useState<EmailConfig>({
    server: 'mail.linuxcorp.cl',
    username: 'anubis@linuxcorp.cl',
    password: '4n00bies.$_240829',
    imapPort: 993,
    smtpPort: 465,
    ssl: true,
    displayName: 'Anubis AI'
  });

  const [showPassword, setShowPassword] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!config.server.trim()) {
      newErrors.server = 'El servidor es requerido';
    }

    if (!config.username.trim()) {
      newErrors.username = 'El usuario es requerido';
    } else if (!config.username.includes('@')) {
      newErrors.username = 'Debe ser un email válido';
    }

    if (!config.password.trim()) {
      newErrors.password = 'La contraseña es requerida';
    }

    if (config.imapPort < 1 || config.imapPort > 65535) {
      newErrors.imapPort = 'Puerto IMAP inválido (1-65535)';
    }

    if (config.smtpPort < 1 || config.smtpPort > 65535) {
      newErrors.smtpPort = 'Puerto SMTP inválido (1-65535)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;

    setTestingConnection(true);
    setConnectionStatus('idle');

    try {
      // Llamada real al backend para probar la conexión
      const response = await fetch('https://backend.anubisai.net/api/pmail/test-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          server: config.server,
          username: config.username,
          password: config.password,
          imapPort: config.imapPort,
          smtpPort: config.smtpPort,
          ssl: config.ssl
        })
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setConnectionStatus('success');
      } else {
        setConnectionStatus('error');
        console.error('Connection test failed:', result.error || result.message);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      setConnectionStatus('error');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = () => {
    if (!validateForm()) return;
    onSave(config);
  };

  const handleInputChange = (field: keyof EmailConfig, value: string | number | boolean) => {
    setConfig(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal
      centered
      show={show}
      onHide={onHide}
      contentClassName="border"
      size="lg"
      backdrop="static"
      scrollable
    >
      <Modal.Header className="ps-card border-bottom border-translucent bg-primary text-white">
        <div>
          <Modal.Title className="modal-title text-white mb-0 d-flex align-items-center">
            <FontAwesomeIcon icon={faCog} className="me-2" />
            Configuración de Email
            <Badge bg="info" className="ms-2 fs-7">Zimbra</Badge>
          </Modal.Title>
        </div>
        <Button 
          className="p-1 ms-auto btn-close-white text-white" 
          onClick={onHide}
          disabled={loading || testingConnection}
        >
          <FontAwesomeIcon icon={faTimes} className="fs-8" />
        </Button>
      </Modal.Header>

      <Modal.Body className="px-card pb-card pt-3" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {/* Estado de conexión */}
        {connectionStatus === 'success' && (
          <Alert variant="success" className="mb-4">
            <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
            <strong>¡Conexión exitosa!</strong> Todos los parámetros están configurados correctamente.
          </Alert>
        )}

        {connectionStatus === 'error' && (
          <Alert variant="danger" className="mb-4">
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
            <strong>Error de conexión</strong> Verifique los datos ingresados. Revise servidor, credenciales y puertos.
          </Alert>
        )}

        <Form>
          {/* Configuración del servidor */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <FontAwesomeIcon icon={faServer} className="me-2" />
              <strong>Configuración del Servidor</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faServer} className="me-2 text-primary" />
                      Servidor de correo
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={config.server}
                      onChange={(e) => handleInputChange('server', e.target.value)}
                      placeholder="mail.example.com"
                      isInvalid={!!errors.server}
                      disabled={loading || testingConnection}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.server}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Dirección del servidor Zimbra
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-success" />
                      Seguridad
                    </Form.Label>
                    <Form.Check 
                      type="switch"
                      id="ssl-switch"
                      label="SSL/TLS"
                      checked={config.ssl}
                      onChange={(e) => handleInputChange('ssl', e.target.checked)}
                      disabled={loading || testingConnection}
                    />
                    <Form.Text className="text-muted">
                      Conexión segura recomendada
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Puerto IMAP</Form.Label>
                    <Form.Control
                      type="number"
                      value={config.imapPort}
                      onChange={(e) => handleInputChange('imapPort', parseInt(e.target.value) || 993)}
                      min="1"
                      max="65535"
                      isInvalid={!!errors.imapPort}
                      disabled={loading || testingConnection}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.imapPort}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Puerto para recibir emails (993 con SSL)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Puerto SMTP</Form.Label>
                    <Form.Control
                      type="number"
                      value={config.smtpPort}
                      onChange={(e) => handleInputChange('smtpPort', parseInt(e.target.value) || 465)}
                      min="1"
                      max="65535"
                      isInvalid={!!errors.smtpPort}
                      disabled={loading || testingConnection}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.smtpPort}
                    </Form.Control.Feedback>
                    <Form.Text className="text-muted">
                      Puerto para enviar emails (465 con SSL)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Credenciales */}
          <Card className="mb-4">
            <Card.Header className="bg-light">
              <FontAwesomeIcon icon={faUser} className="me-2" />
              <strong>Credenciales de Acceso</strong>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faEnvelope} className="me-2 text-info" />
                      Usuario (Email)
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={config.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      placeholder="usuario@linuxcorp.cl"
                      isInvalid={!!errors.username}
                      disabled={loading || testingConnection}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.username}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FontAwesomeIcon icon={faUser} className="me-2 text-secondary" />
                      Nombre a mostrar
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={config.displayName}
                      onChange={(e) => handleInputChange('displayName', e.target.value)}
                      placeholder="Tu Nombre"
                      disabled={loading || testingConnection}
                    />
                    <Form.Text className="text-muted">
                      Nombre que aparecerá al enviar emails
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  <FontAwesomeIcon icon={faLock} className="me-2 text-warning" />
                  Contraseña
                </Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type={showPassword ? 'text' : 'password'}
                    value={config.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder="••••••••"
                    isInvalid={!!errors.password}
                    style={{ paddingRight: '40px' }}
                    disabled={loading || testingConnection}
                  />
                  <Button
                    variant="link"
                    className="position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                    style={{ zIndex: 5 }}
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading || testingConnection}
                  >
                    <FontAwesomeIcon 
                      icon={showPassword ? faEyeSlash : faEye} 
                      className="text-muted"
                    />
                  </Button>
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.password}
                </Form.Control.Feedback>
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Resumen de configuración */}
          <Card className="mb-4 bg-light">
            <Card.Body className="py-3">
              <h6 className="card-title mb-3">
                <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
                Resumen de Configuración
              </h6>
              <Row className="g-2">
                <Col xs={6} md={3}>
                  <div className="text-center">
                    <div className="text-muted small">Servidor</div>
                    <div className="fw-bold small">{config.server || '---'}</div>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center">
                    <div className="text-muted small">Usuario</div>
                    <div className="fw-bold small text-truncate" title={config.username}>
                      {config.username || '---'}
                    </div>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center">
                    <div className="text-muted small">IMAP/SMTP</div>
                    <div className="fw-bold small">{config.imapPort}/{config.smtpPort}</div>
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="text-center">
                    <div className="text-muted small">Seguridad</div>
                    <Badge bg={config.ssl ? 'success' : 'warning'}>
                      {config.ssl ? 'SSL/TLS' : 'Sin SSL'}
                    </Badge>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Botón de prueba de conexión */}
          <div className="text-center mb-3">
            <Button 
              variant="outline-info" 
              onClick={handleTestConnection}
              disabled={testingConnection || loading}
            >
              {testingConnection ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Probando conexión...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faPlug} className="me-2" />
                  Probar Conexión
                </>
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-end px-card pt-0 border-top-0">
        <Button 
          variant="phoenix-secondary" 
          size="sm"
          onClick={onHide}
          disabled={loading || testingConnection}
        >
          <FontAwesomeIcon icon={faTimes} className="me-2" />
          Cancelar
        </Button>
        <Button 
          variant="primary" 
          size="sm"
          onClick={handleSave}
          disabled={loading || testingConnection || Object.keys(errors).length > 0}
        >
          {loading ? (
            <>
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Guardando...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Guardar Configuración
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmailSettingsModal;
export type { EmailConfig };